import React, {useEffect, useState} from 'react';
import {
    Box,
    Container,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Divider,
    TextField,
    Tooltip,
    useTheme,
    useMediaQuery,
    Chip,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import instance from '../../../instance';
import CallNotification from '../../../context/NotificationContext';
import HelpPopover from '../../../ui/HelpPopover';
import {PaymentMethod} from '../../../Enums/PaymentMethod';
import InvoicePopup from '../../PaymentPage/Invoice/InvoicePopup';
import {
    IPayment,
    IPaymentClient,
    IPaymentClientGroup,
    IPaymentDetail,
    IPaymentFilter,
    IPaymentSnapshot,
    IPaymentSnapshotItem,
    PeriodPreset,
} from '../../../interfaces/payment/IPaymentHistory';
import {IGetPaymentInfoRequest, IGetPaymentListRequest} from '../../../interfaces/fetch/paymentHistory';

interface IHistoryPage {
    userId: number | null;
}

const toDateInput = (d: Date) => {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const periodPresets: {key: PeriodPreset; label: string}[] = [
    {key: 'two_months', label: '2 месяца'},
    {key: 'year', label: 'Год'},
    {key: 'all', label: 'Всё время'},
];

const presetRange = (preset: PeriodPreset): Pick<IPaymentFilter, 'dateFrom' | 'dateTo'> => {
    const now = new Date();
    switch (preset) {
        case 'two_months':
            return {dateFrom: toDateInput(new Date(now.getFullYear(), now.getMonth() - 1, 1)), dateTo: toDateInput(now)};
        case 'year':
            return {dateFrom: toDateInput(new Date(now.getFullYear(), 0, 1)), dateTo: toDateInput(now)};
        default:
            return {dateFrom: '', dateTo: ''};
    }
};

const defaultFilter = (): IPaymentFilter => ({...presetRange('two_months'), search: ''});

const activePreset = (f: IPaymentFilter): PeriodPreset | null =>
    periodPresets.find(({key}) => {
        const r = presetRange(key);
        return r.dateFrom === f.dateFrom && r.dateTo === f.dateTo;
    })?.key ?? null;

const HistoryPage = ({userId}: IHistoryPage) => {
    const theme = useTheme();
    const isSm = useMediaQuery(theme.breakpoints.down('sm'));

    const [paymentList, setPaymentList] = useState<IPayment[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    const [filter, setFilter] = useState<IPaymentFilter>(defaultFilter);
    const [appliedFilter, setAppliedFilter] = useState<IPaymentFilter>(filter);

    const [selectedPayment, setSelectedPayment] = useState<IPaymentDetail | null>(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const {handleErrorNotification} = CallNotification();

    const [invoiceOpen, setInvoiceOpen] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

    const openInvoice = (orderId: string) => {
        setSelectedOrderId(orderId);
        setInvoiceOpen(true);
    };

    useEffect(() => {
        if (!userId) return;

        const fetchPaymentList = async () => {
            setLoading(true);
            try {
                const request: IGetPaymentListRequest = {
                    method: 'GetPaymentList',
                    params: {
                        user_id: userId,
                        date_from: appliedFilter.dateFrom || undefined,
                        date_to: appliedFilter.dateTo || undefined,
                        search: appliedFilter.search.trim() || undefined,
                    },
                };
                const response = await instance.post('/LK', request);
                handleErrorNotification(response);

                setPaymentList(response.data?.payment_list || []);
            } catch (error) {
                console.error('Ошибка при загрузке истории заказов', error);
            } finally {
                setLoading(false);
            }
        };

        void fetchPaymentList();
    }, [userId, appliedFilter]);

    const handleRowClick = async (orderCode: string) => {
        if (!userId) return;
        try {
            const request: IGetPaymentInfoRequest = {
                method: 'GetPaymentInfo',
                params: {order: orderCode, user_id: userId},
            };
            const response = await instance.post('/LK', request);
            handleErrorNotification(response);

            if (response.data?.payment_info) {
                setSelectedPayment(response.data.payment_info);
                setDetailOpen(true);
            }
        } catch (error) {
            console.error('Ошибка при получении информации о платеже', error);
        }
    };

    const handleClose = () => {
        setDetailOpen(false);
        setSelectedPayment(null);
    };

    const applyFilter = () => {
        const next = {...filter};
        if (next.dateFrom && next.dateTo && next.dateFrom > next.dateTo) {
            [next.dateFrom, next.dateTo] = [next.dateTo, next.dateFrom];
        }
        setFilter(next);
        setAppliedFilter(next);
    };

    const resetFilter = () => {
        const next = defaultFilter();
        setFilter(next);
        setAppliedFilter(next);
    };

    const applyPreset = (preset: PeriodPreset) => {
        const next = {...filter, ...presetRange(preset)};
        setFilter(next);
        setAppliedFilter(next);
    };

    const renderClients = (clients?: IPaymentClient[]) => {
        if (!clients || clients.length === 0) return <>&mdash;</>;
        const label = (c: IPaymentClient) => `${c.name || '—'} (${c.inn || '—'})`;
        if (clients.length === 1) return <>{label(clients[0])}</>;
        return (
            <Stack direction="row" spacing={1} alignItems="center">
                <span>{label(clients[0])}</span>
                <Tooltip
                    title={
                        <Box component="ul" sx={{m: 0, pl: 2}}>
                            {clients.slice(1).map((c, i) => (
                                <li key={i}>{label(c)}</li>
                            ))}
                        </Box>
                    }
                >
                    <Chip size="small" label={`+${clients.length - 1}`} />
                </Tooltip>
            </Stack>
        );
    };

    const paidOnly = paymentList.filter(p => p.payment_status === 1);
    const paidCount = paidOnly.length;
    const paidTotal = paidOnly.reduce((sum, p) => sum + (p.total_amount || 0), 0);

    const renderPaymentDetails = () => {
        if (!selectedPayment) return null;

        let snapshot: IPaymentSnapshot | null;
        try {
            snapshot = JSON.parse(selectedPayment.payment_snapshot);
        } catch {
            snapshot = null;
        }

        if (!snapshot || !Array.isArray(snapshot.items))
            return (
                <>
                    <Typography variant="subtitle1" gutterBottom>
                        Заказ № {selectedPayment.order_code}
                    </Typography>
                    <Typography variant="body2">Дата: {new Date(selectedPayment.create_date).toLocaleString()}</Typography>
                    <Typography variant="body2">Сумма: {selectedPayment.total_amount} ₸</Typography>
                    <Typography variant="body2">Статус: {selectedPayment.payment_status_name}</Typography>
                </>
            );

        const grouped = snapshot.items.reduce((acc: Record<number, IPaymentClientGroup>, item) => {
            if (!acc[item.facial_id]) {
                acc[item.facial_id] = {
                    facial_name: item.facial_name,
                    facial_inn: item.facial_inn,
                    items: [],
                };
            }
            acc[item.facial_id].items.push(item);
            return acc;
        }, {});

        const getPriceString = (item: IPaymentSnapshotItem) => {
            if (item.quantity && item.quantity > 1) {
                return `${item.quantity} x ${item.unit_price_no_vat} = ${item.price_no_vat}`;
            }
            return item.price_no_vat;
        };

        return (
            <>
                <Typography variant="subtitle1" gutterBottom>
                    Заказ № {selectedPayment.order_code}
                </Typography>
                <Typography variant="body2">Дата: {new Date(selectedPayment.create_date).toLocaleString()}</Typography>
                <Typography variant="body2">Сумма: {selectedPayment.total_amount} ₸</Typography>
                <Typography variant="body2">Статус: {selectedPayment.payment_status_name}</Typography>

                <Divider sx={{my: 2}} />

                {Object.values(grouped).map((group, idx) => (
                    <Box
                        key={idx}
                        sx={{
                            mb: 3,
                            overflowX: 'auto',
                        }}
                    >
                        <Typography variant="subtitle2" sx={{mb: 1}}>
                            {group.facial_name} (ИНН: {group.facial_inn})
                        </Typography>
                        <Table
                            size="small"
                            sx={{
                                '& td, & th': {whiteSpace: 'nowrap'},
                            }}
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell>Модель</TableCell>
                                    <TableCell>Серийный №</TableCell>
                                    <TableCell>Рег. №</TableCell>
                                    <TableCell>ОФД</TableCell>
                                    <TableCell>Без НДС</TableCell>
                                    <TableCell>С НДС</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {group.items.map((item, i) => (
                                    <TableRow key={i}>
                                        <TableCell>{item.model || '—'}</TableCell>
                                        <TableCell>{item.serial_number || '—'}</TableCell>
                                        <TableCell>{item.reg_number || '—'}</TableCell>
                                        <TableCell>{item.ofd?.name || '—'}</TableCell>
                                        <TableCell>{getPriceString(item)} ₸</TableCell>
                                        <TableCell>{item.price_with_vat} ₸</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                ))}

                <Divider sx={{my: 2}} />
                <Typography variant="body2">Итого без НДС: {snapshot.total_no_vat} ₸</Typography>
                <Typography variant="body2">
                    НДС{snapshot.vat_rate ? ` ${snapshot.vat_rate * 100}%` : ''}: {snapshot.vat} ₸
                </Typography>
                <Typography variant="body2">Итого с НДС: {snapshot.total_with_vat} ₸</Typography>
            </>
        );
    };

    return (
        <Container maxWidth="xl" sx={{pt: isSm ? 2 : 6, pb: isSm ? 10 : 20, px: isSm ? 1 : 3}}>
            <Typography variant="h4" gutterBottom>
                История заказов
            </Typography>

            <Paper sx={{mb: 2}}>
                <Stack
                    component="form"
                    onSubmit={e => {
                        e.preventDefault();
                        applyFilter();
                    }}
                    direction="row"
                    flexWrap="wrap"
                    alignItems="center"
                    useFlexGap
                    spacing={2}
                    p={2}
                >
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={activePreset(filter)}
                        onChange={(_, preset: PeriodPreset | null) => preset && applyPreset(preset)}
                        sx={{height: 44, '& .MuiToggleButton-root': {textTransform: 'none', px: 2}}}
                    >
                        {periodPresets.map(({key, label}) => (
                            <ToggleButton key={key} value={key}>
                                {label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                    <TextField
                        label="От"
                        type="date"
                        value={filter.dateFrom}
                        onChange={e => setFilter({...filter, dateFrom: e.target.value})}
                        InputLabelProps={{shrink: true}}
                        sx={{width: 170}}
                    />
                    <TextField
                        label="До"
                        type="date"
                        value={filter.dateTo}
                        onChange={e => setFilter({...filter, dateTo: e.target.value})}
                        InputLabelProps={{shrink: true}}
                        sx={{width: 170}}
                    />
                    <TextField
                        label="Поиск"
                        placeholder="ИИН, название, серийный №, № заказа"
                        value={filter.search}
                        onChange={e => setFilter({...filter, search: e.target.value})}
                        sx={{width: {xs: '100%', sm: 320}}}
                    />
                    <Stack direction="row" spacing={1}>
                        <Button type="submit" variant="contained" disableElevation sx={{height: 44, px: 3}}>
                            Найти
                        </Button>
                        <Button variant="text" onClick={resetFilter} sx={{height: 44}}>
                            Сброс
                        </Button>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ml: {md: 'auto'}, textAlign: {md: 'right'}}}>
                        Оплаченных заказов за период: <b>{paidCount}</b>
                        <br />
                        На сумму: <b>{new Intl.NumberFormat('ru-RU').format(paidTotal)} ₸</b>
                    </Typography>
                </Stack>
            </Paper>

            <Paper>
                <Box p={3} sx={{overflowX: 'auto'}}>
                    {loading ? (
                        <CircularProgress />
                    ) : (
                        <Table
                            size="small"
                            sx={{
                                '& td, & th': {whiteSpace: 'nowrap'},
                            }}
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell>Дата</TableCell>
                                    <TableCell>Номер заказа</TableCell>
                                    <TableCell>
                                        Счёт
                                        <HelpPopover content={<>Номер счёта при оплате через банковские реквизиты.</>} />
                                    </TableCell>
                                    <TableCell>Клиенты</TableCell>
                                    <TableCell>Сумма</TableCell>
                                    <TableCell>Статус</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paymentList.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6}>
                                            <Typography color="text.secondary">Нет записей по выбранному фильтру</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paymentList.map((item, index) => (
                                        <TableRow
                                            key={index}
                                            hover
                                            sx={{
                                                cursor: 'pointer',
                                                '& td': {
                                                    borderBottom: index === paymentList.length - 1 ? 'none' : undefined,
                                                },
                                            }}
                                            onClick={() => void handleRowClick(item.order_code)}
                                        >
                                            <TableCell>{new Date(item.create_date).toLocaleString()}</TableCell>
                                            <TableCell>{item.order_code}</TableCell>
                                            <TableCell sx={{paddingTop: 0, paddingBottom: 0}}>
                                                {item?.payment_method === PaymentMethod.Invoice ? (
                                                    <>
                                                        <Tooltip title="Скачать или распечатать">
                                                            <Button
                                                                size="small"
                                                                color="primary"
                                                                variant="text"
                                                                startIcon={<DescriptionOutlinedIcon sx={{fontSize: 16}} />}
                                                                onClick={e => {
                                                                    e.stopPropagation();
                                                                    openInvoice(item.order_code);
                                                                }}
                                                                sx={{fontSize: 12, height: 20}}
                                                            >
                                                                {item.invoice_number}
                                                            </Button>
                                                        </Tooltip>
                                                    </>
                                                ) : (
                                                    <>&mdash;</>
                                                )}
                                            </TableCell>
                                            <TableCell>{renderClients(item.clients)}</TableCell>
                                            <TableCell>{item.total_amount} ₸</TableCell>
                                            <TableCell sx={{color: item.payment_status === 1 ? 'success.main' : 'inherit'}}>
                                                {item.payment_status_name}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    )}
                </Box>
            </Paper>

            <Dialog open={detailOpen} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogTitle>Детали платежа</DialogTitle>
                <DialogContent dividers>{selectedPayment ? renderPaymentDetails() : <Typography>Загрузка...</Typography>}</DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} variant="outlined">
                        Закрыть
                    </Button>
                </DialogActions>
            </Dialog>

            <InvoicePopup
                open={invoiceOpen}
                setOpen={setInvoiceOpen}
                orderId={selectedOrderId}
                userId={userId}
                onClose={() => setInvoiceOpen(false)}
            />
        </Container>
    );
};

export default HistoryPage;
